/* ==========================================================================
   CAELIX — Hero slider
   --------------------------------------------------------------------------
   A custom element rather than an inline script, so Shopify's theme editor
   re-initialises it when the section is re-rendered. An inline script would
   run once and then silently stop working as blocks are added or reordered.

   Accessibility decisions, deliberately:

   - Auto-advance is OFF by default. When a merchant turns it on, a
     play/pause control is always rendered — WCAG 2.2.2 requires a way to
     stop anything that moves automatically for more than five seconds.
   - Auto-advance never starts under prefers-reduced-motion, and the
     crossfade is disabled there too.
   - Auto-advance pauses on hover, on keyboard focus anywhere inside, and
     whenever the tab is hidden.
   - Arrow keys move between slides when a control has focus.
   - Slides that are not current are hidden from assistive tech and removed
     from the tab order, so a screen reader never reads every headline at
     once.
   ========================================================================== */

class CaelixHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) {
      this.removeAttribute('data-has-controls');
      return;
    }

    this.dots = Array.from(this.querySelectorAll('[data-goto]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.playBtn = this.querySelector('[data-playpause]');
    this.liveRegion = this.querySelector('[data-live]');

    this.index = 0;
    this.timer = null;
    this.interval = Math.max(4, Number(this.dataset.interval) || 7) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;

    this.onKey = this.onKey.bind(this);

    this.dots.forEach((d) =>
      d.addEventListener('click', () => {
        this.go(Number(d.dataset.goto));
        this.stop();
      })
    );
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.go(this.index - 1); this.stop(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.go(this.index + 1); this.stop(); });
    if (this.playBtn) this.playBtn.addEventListener('click', () => this.toggle());

    this.addEventListener('keydown', this.onKey);
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resume();
    });
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.pause() : this.resume();
    });

    this.go(0, true);
    if (this.autoplay) this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.removeEventListener('keydown', this.onKey);
  }

  onKey(e) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); this.stop(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); this.stop(); }
  }

  go(next, initial) {
    const n = this.slides.length;
    this.index = ((next % n) + n) % n;

    this.slides.forEach((s, i) => {
      const on = i === this.index;
      s.toggleAttribute('data-current', on);
      s.setAttribute('aria-hidden', String(!on));
      // keep non-current slides out of the tab order
      s.querySelectorAll('a, button').forEach((el) => {
        if (on) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((d, i) => {
      const on = i === this.index;
      d.setAttribute('aria-current', String(on));
      d.toggleAttribute('data-on', on);
    });

    // Announce only on user-driven changes, never on first paint.
    if (!initial && this.liveRegion) {
      this.liveRegion.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
    }
  }

  start() {
    this.stop();
    this.paused = false;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    this.setPlayState(true);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.autoplay = false;
    this.setPlayState(false);
  }

  pause() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
    this.paused = true;
  }

  resume() {
    if (!this.paused || !this.autoplay) return;
    this.paused = false;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  toggle() {
    if (this.timer || this.paused) { this.stop(); }
    else { this.autoplay = true; this.start(); }
  }

  setPlayState(playing) {
    if (!this.playBtn) return;
    this.playBtn.setAttribute('aria-pressed', String(playing));
    this.playBtn.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
    this.playBtn.toggleAttribute('data-playing', playing);
  }
}

if (!customElements.get('caelix-hero-slider')) {
  customElements.define('caelix-hero-slider', CaelixHeroSlider);
}
